import { useParams } from 'react-router-dom';
import { ServiceCard } from '@/components/service-page/ServiceCard';
import { SearchAndCategories } from '@/components/service-page/SearchAndCategories';
import { ServiceBanner } from '@/components/service-page/ServiceBanner';
import { useServicePage } from '@/utils/useServicePage';
import { useToast } from "@/components/ui/use-toast";
import { useAnalytics } from "@/hooks/useAnalytics";
import { ServiceCardSkeleton } from '@/components/service-page/ServiceCardSkeleton';
import { SearchAndCategoriesSkeleton } from '@/components/service-page/SearchAndCategoriesSkeleton';
import { NoServicesMessageSkeleton } from '@/components/service-page/NoServicesMessageSkeleton';
import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { MetaTags } from '@/components/shared/MetaTags';
import { useTranslation } from "react-i18next";

const ServicePage = () => {
  const { username } = useParams<{ username: string }>();
  const { toast } = useToast();
  // Use analytics hook with pageView set to true for automatic tracking
  const { trackAction } = useAnalytics({ component: "ServicePage", pageView: true });
  const { t } = useTranslation();
  
  // Explicitly control loading state
  const [showSkeleton, setShowSkeleton] = useState(true);
  // Force initial mount delay
  const [mounted, setMounted] = useState(false);
  
  // Force skeletons to show during initial component mount
  useEffect(() => {
    // Force skeletons to be visible for at least 1 second on initial load
    const timer = setTimeout(() => {
      console.log('Component mounted');
      setMounted(true);
    }, 200); 
    
    return () => clearTimeout(timer);
  }, []);
  
  // Use the custom hook for all data fetching and state management
  const {
    profile,
    services,
    categories,
    isLoading,
    servicePageNotFound,
    searchTerm,
    categoryId,
    setSearchTerm,
    setCategoryId,
    handleImageNavigation,
  } = useServicePage(username || '');

  // Ensure skeleton loading shows properly
  useEffect(() => {
    console.log('Loading state changed:', { isLoading, servicesLength: services?.length || 0, mounted, profile: !!profile });
    
    // Always show skeleton if not mounted yet or still loading
    if (!mounted || isLoading) {
      setShowSkeleton(true);
      return;
    }
    
    // Make sure we have both the profile and services data before hiding skeleton
    if (!isLoading && services !== undefined && profile !== null && mounted) {
      // Add deliberate delay to ensure smooth transition
      const timer = setTimeout(() => {
        console.log('Showing content after delay');
        setShowSkeleton(false);
      }, 300); // Longer delay to ensure it's visible
      
      return () => clearTimeout(timer);
    }
    
    // Keep showing skeleton if we don't have all the data yet
    setShowSkeleton(true);
  }, [isLoading, services, mounted, profile]);

  const handleServiceSelect = (serviceId: string) => {
    // Track the service selection action
    trackAction("service_selection", { serviceId });
    
    toast({
      title: "Service Request",
      description: `Requesting service`,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Track search action
    if (e.target.value.trim() !== searchTerm.trim()) {
      trackAction("service_search", { searchTerm: e.target.value });
    }
    
    setSearchTerm(e.target.value);
  };

  // Add debug logs
  console.log('ServicePage render', { 
    username, 
    servicePageNotFound, 
    isLoading, 
    showSkeleton,
    servicesLength: services?.length || 0,
    hasProfile: !!profile
  });

  if (servicePageNotFound) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-b from-pink-50 to-white">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">{t('servicePage.servicePageNotFound')}</h1>
        <p className="text-gray-600">{t('servicePage.servicePageNotFoundDescription')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {profile && (
        <MetaTags
          title={profile.company_name || `${profile.first_name} ${profile.last_name}`.trim()}
          description={profile.profile_bio || `Services provided by ${profile.company_name || `${profile.first_name} ${profile.last_name}`.trim()}`}
          imageUrl={profile.banner_image_url || profile.profile_image_url || "/lovable-uploads/f3752283-ff9d-4cc9-b3c2-b633673f0e58.png"}
          url={window.location.href}
          siteName={profile.company_name || `${profile.first_name} ${profile.last_name}`.trim()}
        />
      )}
      
      <ServiceBanner 
        profile={profile}
        isLoading={showSkeleton}
      />

      <main className="container mx-auto px-2 sm:px-4 py-4 sm:py-6">
        {showSkeleton ? (
          <SearchAndCategoriesSkeleton />
        ) : (
          <SearchAndCategories 
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            categories={categories}
            selectedCategoryId={categoryId}
            onCategoryChange={setCategoryId}
          />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mt-4">
          {showSkeleton ? (
            <>
              <ServiceCardSkeleton />
              <ServiceCardSkeleton />
              <ServiceCardSkeleton />
              <ServiceCardSkeleton />
              <ServiceCardSkeleton />
              <ServiceCardSkeleton />
            </>
          ) : services.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <h3 className="text-lg font-medium text-gray-700">{t('servicePage.noServicesFound')}</h3>
              <p className="text-gray-500 mt-2">{t('servicePage.noServicesFoundDescription')}</p>
            </div>
          ) : (
            services.map(service => {
              console.log(`Service ${service.id} detailed info:`, {
                name: service.name,
                images: service.images,
                hasImages: service.images && service.images.length > 0,
                hasMultipleImages: service.images && service.images.length > 1,
                imageUrls: service.images?.map(img => img.image_url),
                imageIds: service.images?.map(img => img.id),
                selectedImageIndex: service.selectedImageIndex
              });
              return (
                <ServiceCard
                  key={service.id}
                  service={service}
                  serviceImages={service.images}
                  selectedImageIndex={service.selectedImageIndex}
                  onImageNavigation={(direction) => {
                    console.log(`Image navigation for ${service.name}:`, {
                      direction,
                      currentIndex: service.selectedImageIndex,
                      totalImages: service.images?.length
                    });
                    handleImageNavigation(service.id, direction);
                  }}
                  onServiceSelect={() => handleServiceSelect(service.id)}
                  currency={profile?.currency}
                  businessName={profile?.company_name}
                />
              );
            })
          )}
        </div>
      </main>

      {/* Create Your Own Page section */}
      <div className="bg-white border-t mt-12 py-8">
        <div className="container mx-auto px-4">
          <div className="bg-pink-50 rounded-lg p-6 max-w-md mx-auto text-center">
            <h3 className="text-lg font-semibold mb-2">{t('servicePage.wantPageLikeThis')}</h3>
            <p className="text-sm text-gray-600 mb-4">{t('servicePage.createOwnPageDescription')}</p>
            <Button 
              className="bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90 text-white mb-4"
              onClick={() => {
                trackAction("create_your_page_click");
                window.location.href = "https://jitume.gebeya.com";
              }}
            >
              {t('servicePage.createYourPage')}
            </Button>
            
            <div>
              <img src="/lovable-uploads/26e98201-c47f-4755-9b35-45a0396ce15b.png" alt="Gebeya Logo" className="h-8 mx-auto" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicePage;
